/**
 * Edit one school, from the platform console.
 *
 * Everything a school's account needs after provisioning that the create form
 * could only guess at: its name, how students get accounts, what its teachers are
 * trusted to do, and who administers it.
 *
 * Two things are deliberately NOT editable here, and both are load-bearing:
 *
 *  - the URL slug. It is shown, read-only. Renaming it would hand this school's
 *    bookmarks, audit trail and future subdomain to whoever claimed the name next,
 *    so `updateTenantBody` omits it at the contract level rather than trusting a
 *    form not to send it.
 *  - the administrator's email address as a string. A person is one account across
 *    every workspace, so rewriting the address would rename an identity that may
 *    also teach at another school. Changing the admin points the school at a
 *    different one instead — see the block at the bottom.
 *
 * Boards ARE editable here, by a super admin only: what a school may teach is
 * CWY's to set (the school cannot), and the server refuses to drop a board the
 * school still runs live classes under, with a sentence this panel shows.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { SIGNUP_POLICIES, type Board, type SignupPolicy } from '@cwy/config/contracts';
import { useClaims } from '@/hooks/useSession';
import { apiErrorMessage, apiGet, apiPatch, apiPost } from '@/lib/api';
import { queryClient } from '@/lib/queryClient';
import { Button, Card, Field, Input, Select } from '@/components/ds';
import { SecretReveal } from '@/components/admin/SecretReveal';
import { QueryState } from '@/components/admin/QueryState';
import { BoardPicker } from '@/components/admin/BoardPicker';

const POLICY_LABEL: Record<SignupPolicy, string> = {
  CLOSED: 'Sign-in only — staff create every account',
  ROSTER_ACTIVATION: 'Students activate a roster record with a code',
  INVITE_CODE: 'Students register with an invitation',
  OPEN_APPROVAL: 'Students request access, staff approve',
};

interface StaffMember {
  id: string;
  role: 'SCHOOL_ADMIN' | 'TEACHER' | 'COUNSELLOR';
  status: string;
  user: { id: string; name: string; email: string };
}

interface TenantDetail {
  id: string;
  name: string;
  slug: string;
  type: 'FIRST_PARTY' | 'SCHOOL';
  signupPolicy: SignupPolicy;
  teachersMayCreateStudents: boolean;
  teachersMayResetStudents: boolean;
  studentsMayEditOwnName: boolean;
  /** The boards CWY set it up to teach. */
  boards: Board[];
  staff: StaffMember[];
}

/** The tenant settings this panel writes, as one editable object. */
interface Draft {
  name: string;
  signupPolicy: SignupPolicy;
  teachersMayCreateStudents: boolean;
  teachersMayResetStudents: boolean;
  studentsMayEditOwnName: boolean;
  boards: Set<Board>;
}

function sameBoards(a: ReadonlySet<Board>, b: readonly Board[]): boolean {
  return a.size === b.length && b.every((board) => a.has(board));
}

const TOGGLES: { key: keyof Draft & `${string}May${string}`; label: string; hint: string }[] = [
  {
    key: 'teachersMayCreateStudents',
    label: 'Teachers may add students',
    hint: 'Only within classrooms they are assigned to.',
  },
  {
    key: 'teachersMayResetStudents',
    label: 'Teachers may reset student passwords',
    hint: 'Again only their own classrooms. Admins always can.',
  },
  {
    key: 'studentsMayEditOwnName',
    label: 'Students may edit their own name',
    hint: 'Leave off where the roster must match school records.',
  },
];

export function SchoolEditPanel({ tenantId, onDone }: { tenantId: string; onDone: () => void }) {
  const superAdmin = useClaims()?.platformRole === 'PLATFORM_SUPER_ADMIN';
  const detail = useQuery({
    queryKey: ['platform', 'tenants', tenantId],
    queryFn: () => apiGet<TenantDetail>(`/platform/tenants/${tenantId}`),
  });

  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState('');
  const [issued, setIssued] = useState<{ title: string; email: string; secret: string } | null>(
    null,
  );
  const [newAdminName, setNewAdminName] = useState('');
  const [newAdminEmail, setNewAdminEmail] = useState('');

  // Seeded from the server once it lands. Not a write, so it is a legitimate
  // effect — the no-writes-from-effects rule is about mutations, and StrictMode's
  // double invoke is harmless for pure local state.
  useEffect(() => {
    if (detail.data && !draft) {
      setDraft({
        name: detail.data.name,
        signupPolicy: detail.data.signupPolicy,
        teachersMayCreateStudents: detail.data.teachersMayCreateStudents,
        teachersMayResetStudents: detail.data.teachersMayResetStudents,
        studentsMayEditOwnName: detail.data.studentsMayEditOwnName,
        boards: new Set(detail.data.boards),
      });
    }
  }, [detail.data, draft]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['platform', 'tenants'] });
  };

  // Boards are sent only when a super admin changed them: an untouched list is
  // not a board change, and the server refuses one from anyone else.
  const boardsChanged = Boolean(
    superAdmin && draft && detail.data && !sameBoards(draft.boards, detail.data.boards),
  );
  const removedBoards =
    draft && detail.data ? detail.data.boards.filter((board) => !draft.boards.has(board)) : [];

  const save = useMutation({
    mutationFn: () => {
      const { boards, ...settings } = draft!;
      return apiPatch(
        `/platform/tenants/${tenantId}`,
        boardsChanged ? { ...settings, boards: [...boards] } : settings,
      );
    },
    onSuccess: () => {
      invalidate();
      void queryClient.invalidateQueries({ queryKey: ['platform', 'tenants', tenantId] });
      onDone();
    },
    onError: (err) => setError(apiErrorMessage(err)),
  });

  const changeAdmin = useMutation({
    mutationFn: () =>
      apiPost<{ email: string; temporaryPassword: string | null }>(
        `/platform/tenants/${tenantId}/admin`,
        { name: newAdminName.trim(), email: newAdminEmail.trim() },
      ),
    onSuccess: (result) => {
      setIssued({
        title: result.temporaryPassword
          ? 'New admin added — give them these details'
          : 'An existing CWY account is now the admin',
        email: result.email,
        secret: result.temporaryPassword ?? '(their existing password is unchanged)',
      });
      setNewAdminName('');
      setNewAdminEmail('');
      invalidate();
      void queryClient.invalidateQueries({ queryKey: ['platform', 'tenants', tenantId] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const resetPassword = useMutation({
    mutationFn: (member: StaffMember) =>
      apiPost<{ temporaryPassword: string }>(`/platform/users/${member.user.id}/reset-password`, {
        reason: 'Reset from the platform console',
      }),
    onSuccess: (result, member) => {
      setIssued({
        title: `New temporary password for ${member.user.name}`,
        email: member.user.email,
        secret: result.temporaryPassword,
      });
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <Card title="Edit school">
      <QueryState isPending={detail.isPending} error={detail.error}>
        {draft && detail.data && (
          <div className="flex flex-col gap-5">
            {issued && (
              <SecretReveal
                title={issued.title}
                identifier={issued.email}
                secret={issued.secret}
                onDismiss={() => setIssued(null)}
              />
            )}
            {error && <p className="text-sm text-danger">{error}</p>}

            <div className="flex flex-wrap gap-3">
              <Field label="School name" className="min-w-[240px] flex-1" required>
                {({ id }) => (
                  <Input
                    id={id}
                    value={draft.name}
                    maxLength={120}
                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  />
                )}
              </Field>
              <Field
                label="URL"
                className="min-w-[240px] flex-1"
                hint="Fixed once the school exists — its links and audit trail depend on it."
              >
                {({ id }) => <Input id={id} value={`/school/${detail.data.slug}`} readOnly />}
              </Field>
            </div>

            {/* Disabled for the same reason as the school console's own copy:
                only “sign-in only” has screens behind it. */}
            <Field
              label="How students get accounts"
              hint="Staff create every account. The other policies are not available yet."
            >
              {({ id }) => (
                <Select
                  id={id}
                  disabled
                  value={draft.signupPolicy}
                  onChange={(e) =>
                    setDraft({ ...draft, signupPolicy: e.target.value as SignupPolicy })
                  }
                >
                  {SIGNUP_POLICIES.map((policy) => (
                    <option key={policy} value={policy}>
                      {POLICY_LABEL[policy]}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <Field
              label="Boards"
              hint={
                superAdmin
                  ? 'A board you add brings its whole curriculum for this academic year. A board can be removed only once the school has no classes under it.'
                  : 'Set by a CWY super admin.'
              }
            >
              {() => (
                <BoardPicker
                  value={draft.boards}
                  disabled={!superAdmin}
                  onChange={(boards) => setDraft({ ...draft, boards })}
                />
              )}
            </Field>

            <div className="flex flex-col gap-2.5">
              {TOGGLES.map((toggle) => (
                <label key={toggle.key} className="flex items-start gap-2.5 text-sm">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={draft[toggle.key]}
                    onChange={(e) => setDraft({ ...draft, [toggle.key]: e.target.checked })}
                  />
                  <span>
                    <span className="font-semibold text-ink">{toggle.label}</span>
                    <span className="block text-xs text-ink-soft">{toggle.hint}</span>
                  </span>
                </label>
              ))}
            </div>

            <div className="flex gap-2">
              <Button
                onClick={() => {
                  setError('');
                  // Dropping a board takes this year's selection under it with
                  // it — worth a second look before it goes.
                  if (
                    boardsChanged &&
                    removedBoards.length > 0 &&
                    !window.confirm(
                      `Remove ${removedBoards.join(' and ')} from ${draft.name}? The school stops seeing that curriculum, and its topic selection under it for this year is cleared.`,
                    )
                  ) {
                    return;
                  }
                  save.mutate();
                }}
                disabled={save.isPending || !draft.name.trim() || draft.boards.size === 0}
              >
                {save.isPending ? 'Saving…' : 'Save changes'}
              </Button>
              <Button variant="secondary" onClick={onDone}>
                Cancel
              </Button>
            </div>

            <hr className="border-line" />

            <div className="flex flex-col gap-3">
              <div>
                <h4 className="text-sm font-bold text-ink">Administrators</h4>
                <p className="text-xs text-ink-soft">
                  A reset issues a temporary password shown once here and signs them out everywhere.
                </p>
              </div>
              {detail.data.staff
                .filter((member) => member.role === 'SCHOOL_ADMIN')
                .map((member) => (
                  <div key={member.id} className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-ink">
                      {member.user.name}{' '}
                      <span className="text-ink-soft">· {member.user.email}</span>
                      {member.status !== 'ACTIVE' && (
                        <span className="text-ink-faint"> · {member.status.toLowerCase()}</span>
                      )}
                    </span>
                    <Button
                      variant="secondary"
                      disabled={resetPassword.isPending}
                      onClick={() => {
                        setError('');
                        if (
                          window.confirm(
                            `Reset ${member.user.name}'s password? They are signed out immediately and must set a new one.`,
                          )
                        ) {
                          resetPassword.mutate(member);
                        }
                      }}
                    >
                      Reset password
                    </Button>
                  </div>
                ))}

              <div className="flex flex-col gap-2 rounded-[10px] bg-slate-50 p-3">
                <p className="text-xs text-ink-soft">
                  Wrong address on the account, or a change of staff? Naming a different
                  administrator attaches or creates that account and suspends the current one — the
                  school is never left without an admin.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Field label="Name" className="min-w-[160px] flex-1">
                    {({ id }) => (
                      <Input
                        id={id}
                        value={newAdminName}
                        maxLength={120}
                        onChange={(e) => setNewAdminName(e.target.value)}
                      />
                    )}
                  </Field>
                  <Field label="Email" className="min-w-[200px] flex-1">
                    {({ id }) => (
                      <Input
                        id={id}
                        type="email"
                        value={newAdminEmail}
                        maxLength={255}
                        onChange={(e) => setNewAdminEmail(e.target.value)}
                      />
                    )}
                  </Field>
                </div>
                <div>
                  <Button
                    variant="secondary"
                    disabled={
                      changeAdmin.isPending || !newAdminName.trim() || !newAdminEmail.trim()
                    }
                    onClick={() => {
                      setError('');
                      changeAdmin.mutate();
                    }}
                  >
                    {changeAdmin.isPending ? 'Changing…' : 'Make this person the admin'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </QueryState>
    </Card>
  );
}
